import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { Star, ArrowRight, CheckCircle, Phone, ChevronDown } from 'lucide-react';
import PropertyCard from '../components/common/PropertyCard';
import EnquiryForm from '../components/common/EnquiryForm';
import { getFeaturedProperties, getProperties } from '../services/api';
import { WHATSAPP_NUMBER, PHONE_NUMBER, CITIES, PROPERTY_TYPES } from '../utils/constants';
import './Home.css';

const STATS = [
  { value: 500, suffix: '+', label: 'Properties Sold' },
  { value: 12,  suffix: '+', label: 'Years Experience' },
  { value: 3,   suffix: '',  label: 'Prime Cities' },
  { value: 98,  suffix: '%', label: 'Client Satisfaction' },
];

const WHY_US = [
  { icon: '🏗️', title: 'In-house Development', desc: 'End-to-end project development with our own architects, planners and construction teams.' },
  { icon: '🔍', title: 'Transparent Transactions', desc: 'Full disclosure of all costs, RERA compliance, and legally vetted documentation.' },
  { icon: '📈', title: 'Investment Advisory', desc: 'Data-driven insights for high-growth opportunities across Tier-1 and emerging markets.' },
  { icon: '🌐', title: 'NRI Services', desc: 'Dedicated support for NRI investors with remote consultation, virtual tours, and legal assistance.' },
  { icon: '🤝', title: 'End-to-End Support', desc: 'From site visits and negotiations to registration and possession - we handle everything.' },
  { icon: '🏆', title: 'Proven Track Record', desc: '500+ successful transactions and 12+ years of trusted real estate expertise.' },
];

const TESTIMONIALS = [
  { name: 'Rajesh Sharma', city: 'Delhi', text: 'Exceptional service! The team helped me find my dream apartment in Sector 150. Their transparency and professionalism set them apart.', rating: 5 },
  { name: 'Priya Mehta', city: 'Bangalore', text: 'As an NRI investor, I was nervous about investing remotely. Smart Property Solutions made the entire process seamless with virtual tours and proper documentation.', rating: 5 },
  { name: 'Ankit Gupta', city: 'Mumbai', text: 'Invested in the Dholera Smart City project based on their advice. The ROI potential is incredible. Highly recommend their investment advisory service.', rating: 5 },
];

const CITY_LIST = [
  { name: 'Delhi NCR', tagline: "India's capital region - premium residential and commercial hub", img: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=800&q=80', path: '/cities/delhi-ncr', count: '200+ Properties' },
  { name: 'Dholera SIR', tagline: "India's first greenfield smart city - unmissable investment", img: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80', path: '/cities/dholera-sir', count: '80+ Properties' },
  { name: 'Ahmedabad', tagline: "Gujarat's largest city - booming realty and infrastructure", img: 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=800&q=80', path: '/cities/ahmedabad', count: '120+ Properties' },
];

const Home = () => {
  const [heroForm, setHeroForm] = useState({ city: '', type: '' });
  const [featuredProps, setFeaturedProps] = useState([]);
  const { ref: statsRef, inView: statsVisible } = useInView({ triggerOnce: true });

  useEffect(() => {
    getFeaturedProperties()
      .then(res => {
        const data = res.data?.data || res.data || [];
        setFeaturedProps(Array.isArray(data) ? data : []);
      })
      .catch(() => setFeaturedProps([]));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(heroForm);
    window.location.href = `/properties?${params.toString()}`;
  };

  return (
    <div className="home">

      {/* Hero */}
      <section className="hero">
        <div className="hero__bg">
          <div className="hero__overlay" />
          <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80" alt="Smart Property Solutions" />
        </div>
        <div className="hero__content container">
          <div className="hero__text fade-in">
            <div className="hero__eyebrow">
              <div className="hero__dot" />
              <span>India's Trusted Real Estate Partner</span>
            </div>
            <h1 className="hero__title">Building the Future.<br/>Delivering Smart<br/><em>Investments.</em></h1>
            <p className="hero__subtitle">Premium real estate development and brokerage services across Delhi NCR, Dholera SIR, and Ahmedabad.</p>
            <div className="hero__ctas">
              <Link to="/properties" className="btn-primary">Explore Properties <ArrowRight size={16}/></Link>
              <a href={`tel:${PHONE_NUMBER}`} className="hero__call"><Phone size={16}/> {PHONE_NUMBER}</a>
            </div>
          </div>
        </div>
        <div className="hero__search">
          <div className="container">
            <form className="search-bar" onSubmit={handleSearch}>
              <div className="search-bar__field">
                <label>City</label>
                <select value={heroForm.city} onChange={e => setHeroForm(p => ({...p, city: e.target.value}))}>
                  <option value="">All Cities</option>
                  {CITY_LIST.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="search-bar__divider" />
              <div className="search-bar__field">
                <label>Property Type</label>
                <select value={heroForm.type} onChange={e => setHeroForm(p => ({...p, type: e.target.value}))}>
                  <option value="">All Types</option>
                  {PROPERTY_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <button type="submit" className="search-bar__btn">Search Properties</button>
            </form>
          </div>
        </div>
        <div className="hero__scroll"><ChevronDown size={20} /></div>
      </section>

      {/* Stats */}
      <section className="stats-bar" ref={statsRef}>
        <div className="container">
          <div className="stats-bar__grid">
            {STATS.map((s, i) => (
              <div className="stat-item" key={i}>
                <div className="stat-item__value">
                  {statsVisible ? <CountUp end={s.value} duration={2.5} /> : '0'}{s.suffix}
                </div>
                <div className="stat-item__label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="about-preview">
        <div className="container">
          <div className="about-preview__grid">
            <div className="about-preview__images">
              <img className="about-img-1" src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80" alt="Real estate" />
              <img className="about-img-2" src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80" alt="Luxury apartment" />
              <div className="about-preview__badge">
                <span className="about-badge-num">12+</span>
                <span className="about-badge-txt">Years of Excellence</span>
              </div>
            </div>
            <div className="about-preview__content">
              <span className="section-label">About Us</span>
              <h2 className="section-title">Trusted Real Estate Partners Since 2013</h2>
              <div className="gold-divider" />
              <p>Smart Property Solutions is a trusted real estate development and brokerage company specializing in residential and commercial properties across India's fastest growing markets.</p>
              <p style={{marginTop:'16px'}}>With an experienced in-house team of architects, planners, and industry professionals, we develop modern spaces and connect investors to high-growth opportunities in Delhi NCR, Dholera SIR, and Ahmedabad.</p>
              <ul className="about-preview__points">
                {['RERA Registered & Compliant','In-house Architecture Team','NRI Investment Specialists','Channel Partner Network'].map(p => (
                  <li key={p}><CheckCircle size={16}/> {p}</li>
                ))}
              </ul>
              <Link to="/about" className="btn-outline-dark" style={{marginTop:'28px',display:'inline-flex'}}>Our Story <ArrowRight size={16}/></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties — from API */}
      <section className="featured-section">
        <div className="container">
          <div className="section-header">
            <div>
              <span className="section-label">Featured Listings</span>
              <h2 className="section-title">Our Highlighted Properties</h2>
              <p className="section-desc">Hand-picked properties across Delhi NCR, Dholera SIR and Ahmedabad - exceptional value for investors and home buyers.</p>
            </div>
            <Link to="/properties" className="btn-outline-dark">View All Properties</Link>
          </div>
          <div className="properties-grid">
            {featuredProps.length > 0
              ? featuredProps.map(p => <PropertyCard key={p.id} property={p} />)
              : (
                <div style={{gridColumn:'1/-1',textAlign:'center',padding:'60px 20px',color:'var(--text-3)'}}>
                  <p style={{fontSize:'15px'}}>No featured properties yet. Mark a property as featured in the admin panel.</p>
                  <Link to="/properties" className="btn-outline-dark" style={{marginTop:'16px',display:'inline-flex'}}>Browse All Properties</Link>
                </div>
              )
            }
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-us">
        <div className="why-us__bg" />
        <div className="container">
          <div className="why-us__header">
            <span className="section-label">Why Choose Us</span>
            <h2 className="section-title light">What Sets Us Apart</h2>
            <div className="gold-divider" style={{margin:'20px auto'}} />
          </div>
          <div className="why-us__grid">
            {WHY_US.map((w, i) => (
              <div className="why-card" key={i}>
                <span className="why-card__icon">{w.icon}</span>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="cities-section">
        <div className="container">
          <span className="section-label">Our Locations</span>
          <h2 className="section-title">Markets We Operate In</h2>
          <div className="cities-grid">
            {CITY_LIST.map(city => (
              <Link to={city.path} className="city-card" key={city.name}>
                <div className="city-card__img">
                  <img src={city.img} alt={city.name} />
                  <div className="city-card__overlay" />
                </div>
                <div className="city-card__content">
                  <h3>{city.name}</h3>
                  <p>{city.tagline}</p>
                  <span className="city-card__count">{city.count}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <div className="container">
          <div style={{textAlign:'center',marginBottom:'56px'}}>
            <span className="section-label">Client Stories</span>
            <h2 className="section-title">What Our Clients Say</h2>
          </div>
          <div className="testimonials__grid">
            {TESTIMONIALS.map((t, i) => (
              <div className="testimonial-card" key={i}>
                <div className="testimonial-card__quote">"</div>
                <div className="testimonial-card__stars">
                  {[...Array(t.rating)].map((_, j) => <Star key={j} size={14} fill="var(--primary)" stroke="none"/>)}
                </div>
                <p>{t.text}</p>
                <div className="testimonial-card__author">
                  <div className="testimonial-card__avatar">{t.name[0]}</div>
                  <div><strong>{t.name}</strong><span>{t.city}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership CTA */}
      <section className="partnership-cta">
        <div className="container">
          <div className="partnership-cta__inner">
            <div className="partnership-cta__content">
              <span className="section-label">Channel Partnership</span>
              <h2>Grow With Us</h2>
              <p>We welcome developers, investors, and channel partners. Join our network and access exclusive listings, co-development opportunities, and high-commission brokerage tie-ups.</p>
              <Link to="/partnership" className="btn-primary">Become a Partner <ArrowRight size={16}/></Link>
            </div>
            <div className="partnership-cta__form">
              <EnquiryForm propertyTitle="Partnership Enquiry" variant="default"/>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
};

export default Home;
